import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Clock, Coffee, Play, Square, AlertTriangle, Timer } from 'lucide-react';
import { formatTime, formatMinutes } from './utils';

export const ClockCard = ({
  status,
  isWorking,
  isOnBreak,
  isClockedOut,
  liveWorkingMinutes,
  actionLoading,
  onAction,
}) => {
  return (
    <Card className="border-border/60">
      <CardContent className="pt-6">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 md:gap-6">
          {/* Status Display */}
          <div className="flex items-center gap-3 sm:gap-4">
            <div className={`p-3 sm:p-4 rounded-full flex-shrink-0 ${
              isWorking ? 'bg-green-100 text-green-600' :
              isOnBreak ? 'bg-amber-100 text-amber-600' :
              'bg-gray-100 text-gray-500'
            }`}>
              {isOnBreak ? <Coffee className="h-6 w-6 sm:h-8 sm:w-8" /> : <Clock className="h-6 w-6 sm:h-8 sm:w-8" />}
            </div>
            <div className="min-w-0">
              <p className="text-base sm:text-lg font-medium">
                {isWorking ? 'Sie arbeiten' : isOnBreak ? 'In Pause' : 'Nicht eingestempelt'}
              </p>
              {status?.clock_in && (
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Beginn: {formatTime(status.clock_in)}
                  {status.break_time > 0 && ` • Pausen: ${status.break_time} Min`}
                </p>
              )}
            </div>
          </div>

          {/* Live Work Time */}
          {status?.clock_in && !isClockedOut && (
            <div className="text-center">
              <p className="text-xs sm:text-sm text-muted-foreground">Aktuelle Arbeitszeit</p>
              <p className="text-2xl sm:text-3xl font-mono font-bold">
                {formatMinutes(liveWorkingMinutes)}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full md:w-auto">
            {isClockedOut ? (
              <Button
                size="lg"
                className="rounded-full w-full md:w-auto px-6 sm:px-8 bg-green-600 hover:bg-green-700"
                onClick={() => onAction('clock-in')}
                disabled={actionLoading}
                data-testid="clock-in-btn"
              >
                <Play className="h-5 w-5 mr-2" />
                Kommen
              </Button>
            ) : (
              <>
                {isWorking && (
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-full w-full sm:w-auto px-4 sm:px-6"
                    onClick={() => onAction('break-start')}
                    disabled={actionLoading}
                    data-testid="break-start-btn"
                  >
                    <Coffee className="h-5 w-5 mr-2" />
                    Pause
                  </Button>
                )}
                {isOnBreak && (
                  <Button
                    size="lg"
                    className="rounded-full w-full sm:w-auto px-4 sm:px-6 bg-amber-600 hover:bg-amber-700"
                    onClick={() => onAction('break-end')}
                    disabled={actionLoading}
                    data-testid="break-end-btn"
                  >
                    <Play className="h-5 w-5 mr-2" />
                    Weiter
                  </Button>
                )}
                <Button
                  size="lg"
                  variant="destructive"
                  className="rounded-full w-full sm:w-auto px-6 sm:px-8"
                  onClick={() => onAction('clock-out')}
                  disabled={actionLoading}
                  data-testid="clock-out-btn"
                >
                  <Square className="h-5 w-5 mr-2" />
                  Gehen
                </Button>
              </>
            )}
          </div>
        </div>

        {/* ArbZG Compliance Warnings */}
        {status?.compliance_warnings && status.compliance_warnings.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="compliance-warnings">
            {status.compliance_warnings.map((warning, i) => (
              <div key={`cw-${i}`} className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 dark:text-red-200">{warning}</p>
              </div>
            ))}
          </div>
        )}

        {/* §3 ArbZG: Max Hours Warning */}
        {status?.max_hours_check && !status.max_hours_check.ok && (
          <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2" data-testid="max-hours-warning">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 dark:text-red-200">{status.max_hours_check.warning}</p>
          </div>
        )}
        {status?.max_hours_check?.approaching && (isWorking || isOnBreak) && (
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-2" data-testid="max-hours-approaching">
            <Timer className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-200">{status.max_hours_check.info}</p>
          </div>
        )}

        {/* ArbZG Break Warning (existing) */}
        {status?.compliance_warning && (
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-200">{status.compliance_warning}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
